import { create } from '@/shared/lib/zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

type FacilityModeState = {
  /** `null` = All Facilities (Portfolio Mode). */
  selectedFacilityId: string | null;
  /**
   * Set when a legacy `/facilities/<id>` link asked for a facility, and cleared
   * once we know whether it resolved. Lets us tell the user their link pointed
   * at a facility they cannot open, instead of silently dropping them on All
   * Facilities and looking broken.
   */
  requestedFacilityId: string | null;
};

type FacilityModeActions = {
  selectFacility: (facilityId: string) => void;
  clearSelection: () => void;
  requestFacility: (facilityId: string) => void;
  clearRequestedFacility: () => void;
};

const initialState: FacilityModeState = {
  selectedFacilityId: null,
  requestedFacilityId: null,
};

/**
 * Global facility mode — the single source of truth for whether the platform is
 * showing one facility or the whole portfolio.
 *
 * Persisted to **sessionStorage**, not localStorage: the selection must survive
 * navigation and reloads within a session, but reset to "All Facilities" on the
 * next login.
 *
 * Created via the shared `create` wrapper so `resetAllStores()` on logout clears
 * it automatically.
 */
export const useFacilityModeStore = create<
  FacilityModeState & FacilityModeActions
>()(
  persist(
    immer((set) => ({
      ...initialState,
      selectFacility: (facilityId) => {
        set((state) => {
          state.selectedFacilityId = facilityId;
        });
      },
      clearSelection: () => {
        set((state) => {
          state.selectedFacilityId = null;
        });
      },
      requestFacility: (facilityId) => {
        set((state) => {
          state.selectedFacilityId = facilityId;
          state.requestedFacilityId = facilityId;
        });
      },
      clearRequestedFacility: () => {
        set((state) => {
          state.requestedFacilityId = null;
        });
      },
    })),
    {
      name: 'facility-mode',
      storage: createJSONStorage(() => sessionStorage),
      // requestedFacilityId is a one-shot signal for the current navigation --
      // persisting it would re-fire the message on every reload.
      partialize: (state) => ({ selectedFacilityId: state.selectedFacilityId }),
    }
  )
);

export const resetFacilityMode = () =>
  useFacilityModeStore.getState().clearSelection();
